import type { Carer, HomeDto, Resident } from '@lmc/ts-common'
import { UserRole } from '@lmc/ts-common'
import {
  Add,
  Button,
  Descriptions,
  Edit,
  EmptyState,
  FormCard,
  NotStartedIcon,
  PageHeader
} from '@lmc/ui-kit'
import { canManagePayAndInvoicing } from 'common/permissions'
import { getNominative } from 'common/utils'
import useCanEditServiceUserData from 'components/AccessControl/useCanEditServiceUserData'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import useUser from 'hooks/useUser/useUser'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../components'
import { useGetChargeRates } from '../hooks/useChargeRates'
import { useGetInvoicingSettings } from '../hooks/useInvoicingRates'
import { RotaPatternModal } from '../RotaPattern'
import NewRotaPatternModal from '../RotaPattern/NewRotaPatternModal/NewRotaPatternModal'
import RotaVisitsTable from '../RotaPattern/RotaVisitsTable'
import {
  getFundingSourceName,
  getIsCarePackagesEmpty,
  labelStyle
} from '../utils'
import { TIME_LAST_DATE_FORMAT } from '../utils.datetime'
export const CarePackageAndPatternsScreen = () => {
  const { t } = useTranslation()
  const canEditServiceUserData = useCanEditServiceUserData()

  const { hasRota } = useHomeType()

  const { location, navigate, params } = useRouter()

  const { resident } = useResident({ id: params?.id })

  const residentWithPackages = resident as unknown as Resident

  const { user: userUpdatedBy } = useUser({
    id: residentWithPackages?.carePackage?.updatedBy as unknown as string
  })

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const residentNominative = getNominative(
    home.preferredPeopleNominative,
    'Resident'
  )

  const hasEmptyCarePackageEmpty = getIsCarePackagesEmpty(residentWithPackages)

  const hasAdditionalNotes = !!resident?.carePackage?.additionalNotes

  const [patternModalVisible, setPatternModalVisible] = useState(false)

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_dev_features',
      'new_create_pattern',
      'rota_invoicing'
    ]
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const flagsNewCreatePatternEnabled = flags?.new_create_pattern?.enabled

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const canManageChargeRates = canManagePayAndInvoicing(userRole)
  const isAuditView = userRole === UserRole.auditView

  const { data: invoicingSettings } = useGetInvoicingSettings()
  const showChargeRates =
    flags?.rota_invoicing?.enabled &&
    invoicingSettings?.enabled &&
    canManageChargeRates

  const { data: chargeRatesData } = useGetChargeRates(
    showChargeRates ? resident?.id : undefined
  )

  const descriptionsCardItems = [
    ...(residentWithPackages?.carePackage?.visitNotes
      ? [
          {
            key: 1,
            label: t('VISIT_PREFERENCES'),
            children: residentWithPackages?.carePackage?.visitNotes
          }
        ]
      : [])
  ]

  const fundingSourceCardItems = resident?.carePackage?.fundingSources || []

  const hasTotalField = fundingSourceCardItems?.length >= 1

  // Test ID constants following best practices
  const baseTestId = 'care-package'
  const editButtonTestId = commonPatterns.button(baseTestId, 'edit')
  const carePackagesListTestId = `${baseTestId}-list`
  const carePackageItemTestId = `${baseTestId}-item`
  const addVisitButtonTestId = commonPatterns.button(baseTestId, 'add-visit')
  const historyButtonTestId = commonPatterns.button(baseTestId, 'history')
  const emptyStateTestId = `${baseTestId}-empty-state`
  const informationSectionTestId = `${baseTestId}-information-section`
  const visitPatternsSectionTestId = `${baseTestId}-visit-patterns-section`
  const rotaVisitsTableTestId = `${baseTestId}-rota-visits-table`
  const otherCardTestId = `${baseTestId}-other-card`

  return (
    <>
      <PageHeader title={t('CARE_PACKAGE_AND_PATTERNS')} sticky>
        <ButtonsContainer>
          {flagsRosteringDevFeaturesEnabled && (
            <Button data-testid={historyButtonTestId}>{t('HISTORY')}</Button>
          )}
          {!isAuditView && (
            <Button
              variant="primary"
              disabledByPermission={!canEditServiceUserData}
              onClick={() => navigate(`${location.pathname}/edit`)}
              data-testid={editButtonTestId}
            >
              <Edit />
              {t('EDIT')}
            </Button>
          )}
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        {!hasEmptyCarePackageEmpty && (
          <>
            <SectionHeader
              title={t('INFORMATION')}
              subtitle={t('LAST_UPDATED_DATE_BY', {
                date: dayjs(residentWithPackages?.carePackage?.editedOn).format(
                  TIME_LAST_DATE_FORMAT
                ),
                user: userUpdatedBy?.name
                  ? `${userUpdatedBy?.name?.first} ${userUpdatedBy?.name?.last}`
                  : '',
                by: userUpdatedBy?.name ? '- By' : ''
              })}
              testId={informationSectionTestId}
            />
            <CardsContainer data-testid={carePackagesListTestId}>
              {!!fundingSourceCardItems?.length && (
                <InfoCardContainer
                  $singleCard={!!fundingSourceCardItems?.length}
                  data-testid={carePackageItemTestId}
                >
                  <InfoCard title={t('CARE_PACKAGE')}>
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={labelStyle}
                      items={[
                        ...(hasTotalField
                          ? [
                              {
                                key: 'total',
                                label: t('TOTAL_HOURS_PER_WEEK'),
                                children:
                                  residentWithPackages?.carePackage?.fundingSources?.reduce(
                                    (total, item) => item.hoursPerWeek + total,
                                    0
                                  )
                              }
                            ]
                          : []),
                        ...fundingSourceCardItems
                          .filter((source) => {
                            if (!getFundingSourceName(source, home)) {
                              return false
                            }
                            return true
                          })
                          .map((source, index) => {
                            const sourceRates = showChargeRates
                              ? (chargeRatesData ?? []).filter(
                                  (cr) => cr.fundingTypeId === source.type
                                )
                              : []
                            return {
                              key: index,
                              label: getFundingSourceName(source, home),
                              children:
                                sourceRates.length > 0 ? (
                                  <FundingSourceValue>
                                    <span>{source?.hoursPerWeek} hours</span>
                                    {sourceRates.map((cr) => {
                                      if (!cr.visitTypeName) {
                                        return null
                                      }
                                      const unitLabel =
                                        cr.rateType === 'HOURLY'
                                          ? t('RATE_TYPE_HOURLY')
                                          : t('RATE_TYPE_FIXED')
                                      return (
                                        <span key={cr._id ?? cr.eventTypeId}>
                                          {cr.visitTypeName}: {cr.rate}{' '}
                                          {unitLabel}
                                        </span>
                                      )
                                    })}
                                  </FundingSourceValue>
                                ) : (
                                  `${source?.hoursPerWeek} hours`
                                )
                            }
                          }),
                        ...(hasAdditionalNotes
                          ? [
                              {
                                key: fundingSourceCardItems.length + 1,
                                label: t('ADDITIONAL_NOTES'),
                                children: resident?.carePackage?.additionalNotes
                              }
                            ]
                          : [])
                      ]}
                    />
                  </InfoCard>
                </InfoCardContainer>
              )}
              {!!descriptionsCardItems?.length && (
                <InfoCardContainer
                  $singleCard={!!fundingSourceCardItems?.length}
                  data-testid={otherCardTestId}
                >
                  <InfoCard title={t('OTHER')}>
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={labelStyle}
                      items={descriptionsCardItems}
                    />
                  </InfoCard>
                </InfoCardContainer>
              )}
            </CardsContainer>
            {hasRota && (
              <>
                <SectionHeader
                  title={t('VISIT_PATTERNS')}
                  subtitle={t('VISIT_PATTERNS_DESC')}
                  rightChildren={
                    !isAuditView ? (
                      <Button
                        variant="primary"
                        disabledByPermission={!canEditServiceUserData}
                        onClick={() => setPatternModalVisible(true)}
                        data-testid={addVisitButtonTestId}
                      >
                        <Add />
                        {t('ADD_A_VISIT')}
                      </Button>
                    ) : undefined
                  }
                  testId={visitPatternsSectionTestId}
                />
                <RotaVisitsTable
                  hasResidentsFilter={false}
                  residentId={resident?.id}
                  hasResidentColumn={false}
                  testId={rotaVisitsTableTestId}
                />
              </>
            )}
          </>
        )}
        {hasEmptyCarePackageEmpty && (
          <EmptyState
            title={t('EMPTY_STATE_NOT_STARTED_TITLE')}
            icon={<NotStartedIcon />}
            description={t('EMPTY_STATE_NOT_STARTED_CARE_PACKAGE', {
              nominative: residentNominative
            })}
            testId={emptyStateTestId}
          />
        )}
        {patternModalVisible &&
          (flagsNewCreatePatternEnabled ? (
            <NewRotaPatternModal
              onCancel={() => setPatternModalVisible(false)}
              type="visit"
              residentId={resident?.id}
            />
          ) : (
            <RotaPatternModal
              patternModalVisible={patternModalVisible}
              onCancel={() => setPatternModalVisible(false)}
              type="visit"
            />
          ))}
      </PageWrapper>
    </>
  )
}

const FundingSourceValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const CardsContainer = styled.div`
  display: flex;
  gap: 24px;
`

const InfoCard = styled(FormCard)`
  flex: 1;
  justify-content: flex-start;
`

const InfoCardContainer = styled.div<{ $singleCard: boolean }>`
  width: ${(props) => props?.$singleCard && '50%'};
`
